import { FC } from "react";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { EducationalEngagementByItem } from "../../../../../services/reports.service";

// SIMS-5801 thresholds for access rate:
//   >=70 % teal · 50–69 % amber · <50 % coral
const TEAL = "#1D9E75";
const AMBER = "#F2A93B";
const CORAL = "#D85A30";

const colorFor = (rate: number): string => {
	if (rate >= 70) return TEAL;
	if (rate >= 50) return AMBER;
	return CORAL;
};

interface AccessRateChartProps {
	data: EducationalEngagementByItem[];
	isLoading?: boolean;
}

const AccessRateChart: FC<AccessRateChartProps> = ({ data, isLoading }) => {
	if (isLoading) {
		return (
			<Paper variant="outlined" sx={{ p: 3 }}>
				<Skeleton variant="rectangular" height={320} />
			</Paper>
		);
	}

	if (!data || data.length === 0) {
		return (
			<Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
				<Typography variant="body2" color="text.secondary">
					No content access data yet.
				</Typography>
			</Paper>
		);
	}

	// Reasonable min height so 1–2 items don't render impossibly small,
	// and so 10+ items still have room to breathe.
	const height = Math.max(180, data.length * 36 + 80);

	return (
		<Paper variant="outlined" sx={{ p: 3 }}>
			<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
				Access rate per content item
			</Typography>
			<Box sx={{ width: "100%", height }}>
				<ResponsiveContainer width="100%" height="100%">
					<BarChart
						layout="vertical"
						data={data}
						margin={{ top: 8, right: 24, bottom: 0, left: 24 }}
					>
						<CartesianGrid strokeDasharray="3 3" horizontal={false} />
						<XAxis
							type="number"
							domain={[0, 100]}
							tickFormatter={(v: number) => `${v}%`}
						/>
						<YAxis
							type="category"
							dataKey="title"
							width={180}
							tick={{ fontSize: 12 }}
						/>
						<Tooltip
							formatter={(value: number) => [`${value}%`, "Access rate"]}
						/>
						<Bar dataKey="accessRate">
							{data.map((entry) => (
								<Cell key={entry.itemId} fill={colorFor(entry.accessRate)} />
							))}
						</Bar>
					</BarChart>
				</ResponsiveContainer>
			</Box>
		</Paper>
	);
};

export default AccessRateChart;
